import { Button, TextButton } from '@creditornot/cb-button';
import { InputCheckbox } from '@creditornot/cb-checkbox';
import { colors, cbSemanticCssVars, typographyCss } from '@creditornot/cb-ingredients';
import { ModalFooter } from '@creditornot/cb-modal';
import { cssThemedValue } from '@creditornot/cb-sc-utils';
import React from 'react';
import { Field, useForm } from 'react-final-form';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { ModalLayout } from './ModalLayout';
import { VenueSelectItem, VenueSelectorForm, VenueRowProps } from './types';
import { useSearchVenues } from './useSearchVenues';

type EditModalContentProps<T> = {
  rowRenderer: (props: VenueRowProps<T>) => React.ReactNode;
  venueSelectItems: VenueSelectItem<T>[];
  onClose(): void;
  onUpdate(): void;
};

const VenueListWrapper = styled.div`
  overflow-y: auto;
  max-height: 600px;
  display: flex;
  flex-direction: column;
`;

const ButtonsContainer = styled.div`
  display: flex;
  gap: 8px;
`;

const ListRow = styled.label`
  cursor: pointer;
  &:hover {
    background-color: ${cssThemedValue({ light: colors.pepper8, dark: colors.salt4 })};
  }
  display: flex;
  align-items: center;
  gap: 16px;
`;

const VenueList = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1 1 300px;
  > ${ListRow} {
    padding: 8px 16px;
    border-block-end: 1px solid ${cssThemedValue({ light: colors.pepper8, dark: colors.salt12 })};
  }
`;

const StyledTextButton = styled(TextButton)`
  ${typographyCss.Body3()};
  font-weight: 500;
`;

const VenuesCount = styled.div`
  ${typographyCss.Body3()};
  color: ${cbSemanticCssVars.textSecondary};
  font-weight: 500;
`;

const SelectActionsRoot = styled.div`
  display: flex;
  gap: 16px;
`;

const StyledFooter = styled(ModalFooter)`
  display: flex;
  justify-content: space-between;
`;

export const EditModalContent = <T,>({
  venueSelectItems,
  onClose,
  onUpdate,
  rowRenderer,
}: EditModalContentProps<T>) => {
  const { searchValue, setSearchValue, venues: filteredVenueSelectItems } = useSearchVenues(venueSelectItems);
  const form = useForm<VenueSelectorForm>();
  const formValues = form.getState().values;

  return (
    <ModalLayout onClose={onClose} searchValue={searchValue} setSearchValue={setSearchValue}>
      <>
        <VenueListWrapper>
          <VenueList>
            {filteredVenueSelectItems.map(venueSelectItem => (
              <ListRow key={venueSelectItem.id}>
                <Field name="venueIds" type="checkbox" value={venueSelectItem.id}>
                  {({ input }) => <InputCheckbox {...input} value={venueSelectItem.id} />}
                </Field>
                {rowRenderer({ venueSelectItem, searchValue })}
              </ListRow>
            ))}
          </VenueList>
        </VenueListWrapper>
        <StyledFooter>
          <SelectActionsRoot>
            {formValues.venueIds.length === 0 ? (
              <StyledTextButton
                disabled={!!searchValue}
                onClick={() =>
                  form.change(
                    'venueIds',
                    venueSelectItems.map(venueSelectItem => venueSelectItem.id),
                  )
                }
              >
                <FormattedMessage id="campaigns.form.venues.select-all" />
              </StyledTextButton>
            ) : (
              <StyledTextButton onClick={() => form.change('venueIds', [])}>
                <FormattedMessage id="campaigns.form.venues.deselect-all" />
              </StyledTextButton>
            )}
            <VenuesCount>
              <FormattedMessage
                id="campaigns.form.venues.selected-venues-count"
                values={{ count: formValues.venueIds.length, total: venueSelectItems.length }}
              />
            </VenuesCount>
          </SelectActionsRoot>
          <ButtonsContainer>
            <Button variant="lightBlue" size="small" onClick={onClose}>
              <FormattedMessage id="campaigns.form.venues.cancel" />
            </Button>
            <Button variant="blue" size="small" onClick={onUpdate}>
              <FormattedMessage id="campaigns.form.venues.select-venues" />
            </Button>
          </ButtonsContainer>
        </StyledFooter>
      </>
    </ModalLayout>
  );
};
